import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Mountain, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { PageHeader } from "@/components/admin/ui";
import { TripDialog } from "@/components/admin/TripDialog";
import { ConfirmDelete } from "@/components/admin/ConfirmDelete";
import { EmptyState, PageLoader } from "@/components/common";
import { useAdminTrips } from "@/hooks/queries";
import { api, formatApiError, invalidateAdmin } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { startPrice } from "@/lib/pricing";

const TripTile = ({ trip, onEdit, onDelete, onToggle }) => (
  <div className={`panel overflow-hidden ${trip.active ? "" : "opacity-60"}`} data-testid={`admin-trip-card-${trip.id}`}>
    <div className="relative h-40">
      <img src={trip.image_url || "/images/hikers.jpg"} alt={trip.name} className="h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
      <div className="absolute bottom-3 left-4 right-4">
        <p className="font-display text-2xl">{trip.name}</p>
        <p className="text-xs text-neutral-400">{trip.location}</p>
      </div>
    </div>
    <div className="space-y-4 p-4">
      <ul className="space-y-1.5 text-sm">
        {trip.packages.map((p) => (
          <li key={p.id} className="flex justify-between gap-3">
            <span className="truncate text-neutral-300">{p.name}</span>
            <span className="whitespace-nowrap font-mono text-gold-light">{rupiah(startPrice(p))}</span>
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-between border-t border-white/10 pt-4">
        <label className="flex items-center gap-2 text-xs text-neutral-400">
          <Switch checked={trip.active} onCheckedChange={() => onToggle(trip)} data-testid={`admin-trip-toggle-${trip.id}`} />
          {trip.active ? "Tampil" : "Disembunyikan"}
        </label>
        <div className="flex gap-1">
          <Button size="icon" variant="ghost" onClick={() => onEdit(trip)} data-testid={`admin-trip-edit-${trip.id}`}><Pencil /></Button>
          <Button size="icon" variant="ghost" className="hover:text-rose-400" onClick={() => onDelete(trip)} data-testid={`admin-trip-delete-${trip.id}`}><Trash2 /></Button>
        </div>
      </div>
    </div>
  </div>
);

export default function Trips() {
  const qc = useQueryClient();
  const { data: trips = [], isLoading } = useAdminTrips();
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const refresh = () => invalidateAdmin(qc);
  const toggle = useMutation({
    mutationFn: (t) => api.put(`/admin/trips/${t.id}`, { ...t, active: !t.active }),
    onSuccess: () => { toast.success("Status trip diperbarui"); refresh(); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/admin/trips/${id}`),
    onSuccess: () => { toast.success("Trip dihapus"); setToDelete(null); refresh(); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  return (
    <div className="space-y-6" data-testid="admin-trips-page">
      <PageHeader title="Trip & harga" subtitle="Lokasi, foto, paket, dan tabel harga yang tampil di website">
        <Button onClick={() => setEditing({})} data-testid="admin-trip-btn-add"><Plus /> Tambah trip</Button>
      </PageHeader>
      {isLoading && <PageLoader className="min-h-[30vh]" />}
      {!isLoading && trips.length === 0 && <EmptyState icon={Mountain} title="Belum ada trip" testId="admin-trips-empty" />}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {trips.map((t) => <TripTile key={t.id} trip={t} onEdit={setEditing} onDelete={setToDelete} onToggle={(x) => toggle.mutate(x)} />)}
      </div>
      {editing && <TripDialog trip={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={refresh} />}
      <ConfirmDelete open={!!toDelete} title="Hapus trip?" description={`Trip ${toDelete?.name} akan dihapus. Pesanan lama tetap tersimpan.`}
        onConfirm={() => remove.mutate(toDelete.id)} onCancel={() => setToDelete(null)} />
    </div>
  );
}
